import { EmailSend } from '../../email/EmailSend';
import CheckRounded from '@mui/icons-material/CheckRounded';
import DescriptionRounded from '@mui/icons-material/DescriptionRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import ReceiptRounded from '@mui/icons-material/ReceiptRounded';
import { Box, Button, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import { Money } from '../../../components/Money';
import type { CounterSale } from '../lib/api';
import { dec } from '../lib/decimal';
import { Kbd } from './bits';
import { WhatsappSend, type WhatsappTarget } from '../../whatsapp';

/** Shown after a counter sale posts: change to hand back, print, start the next sale. */
export function SaleCompleteDialog({
  sale,
  balance,
  onPrintReceipt,
  onPrintA4,
  onNext,
  whatsapp,
}: {
  sale: CounterSale | null;
  /** The customer's balance after this sale, when known (named customers only). */
  balance?: string | null;
  /** Send the receipt on WhatsApp (shown only with the plugin on). */
  whatsapp?: WhatsappTarget | null;
  onPrintReceipt: () => void;
  onPrintA4: () => void;
  onNext: () => void;
}) {
  const navigate = useNavigate();
  const inv = sale?.invoice;
  const change = sale ? dec(sale.changeDue) : 0n;
  const onAccount = inv ? dec(inv.outstanding) : 0n;
  return (
    <Dialog open={!!sale} onClose={onNext} maxWidth="xs" fullWidth>
      {inv && (
        <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 3 }}>
          <Box
            sx={(t) => {
              const c = (t.vars ?? t).palette.success.main;
              return {
                width: 68,
                height: 68,
                mx: 'auto',
                mb: 2,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                color: '#fff',
                background: `linear-gradient(135deg, color-mix(in srgb, ${c} 85%, #fff), ${c})`,
                boxShadow: `0 10px 24px -8px color-mix(in srgb, ${c} 60%, transparent), 0 0 0 8px color-mix(in srgb, ${c} 12%, transparent)`,
                animation: 'sm-pop 260ms cubic-bezier(0.2, 0, 0, 1.3)',
                '@keyframes sm-pop': { from: { transform: 'scale(0.6)', opacity: 0 } },
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              };
            }}
          >
            <CheckRounded sx={{ fontSize: 38 }} />
          </Box>
          <Typography variant="h4">Sale complete</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            {inv.number} · <Money value={inv.total} />
          </Typography>

          <Box
            sx={{
              mt: 3,
              mb: 3,
              py: 2,
              borderRadius: '14px',
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="overline" color="text.secondary">
              {change > 0n
                ? 'Change to give'
                : onAccount > 0n
                  ? 'Added to account'
                  : 'Paid in full'}
            </Typography>
            <Typography
              variant="h1"
              className="tabular"
              sx={{
                fontSize: '2.5rem',
                color:
                  change > 0n ? 'success.main' : onAccount > 0n ? 'warning.main' : 'text.primary',
              }}
            >
              <Money
                value={change > 0n ? sale.changeDue : onAccount > 0n ? inv.outstanding : inv.total}
              />
            </Typography>
            {onAccount > 0n && balance && (
              <Typography variant="body2" color="text.secondary" data-testid="new-balance">
                {inv.contact.name}’s balance is now <Money value={balance} />
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={1} sx={{ mb: 1.25 }}>
            <Button
              variant="outlined"
              startIcon={<ReceiptRounded />}
              onClick={onPrintReceipt}
              sx={{ flex: 1 }}
            >
              Receipt
            </Button>
            <Button
              variant="outlined"
              startIcon={<DescriptionRounded />}
              onClick={onPrintA4}
              sx={{ flex: 1 }}
            >
              A4 invoice
            </Button>
          </Stack>
          {whatsapp && (
            <Stack spacing={1} sx={{ mb: 1.25, '&:empty': { display: 'none' } }}>
              <WhatsappSend target={whatsapp} label="Send on WhatsApp" fullWidth />
              <EmailSend target={whatsapp} label="Send by email" fullWidth />
            </Stack>
          )}
          <Button
            fullWidth
            variant="contained"
            size="large"
            autoFocus
            onClick={onNext}
            endIcon={<Kbd inverse>Enter</Kbd>}
            sx={{ minHeight: 50 }}
          >
            Next sale
          </Button>
          <Button
            size="small"
            sx={{ mt: 1.5 }}
            endIcon={<OpenInNewRounded fontSize="small" />}
            onClick={() => void navigate(`/sales/invoices/${inv.id}`)}
          >
            View invoice
          </Button>
        </DialogContent>
      )}
    </Dialog>
  );
}
